import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Send, Mail, MessageSquare, Loader2, X } from 'lucide-react';
import { Card } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Switch } from '@/components/atoms/switch';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { formatPhoneDisplay } from '@/lib/contacts/phone';
import type { SendChannels } from '@/hooks/estimates/useSendEstimate';
import type { CustomerApprovalMethod } from '@/types/estimate';
import { ESTIMATE_APPROVAL_METHOD_LABELS } from '@/types/settings';

type Props = {
  open: boolean;
  title: string;
  customerName: string;
  customerEmail?: string | null;
  customerPhone?: string | null;
  /**
   * NOT-584 (A4) — how the customer will be asked to approve. Opt-in: the
   * picker renders only when a caller supplies both the value and the handler.
   * Estimates pass them; proposals deliberately do not, and are unchanged.
   *
   * The choice used to live on the estimate form. Nobody decides
   * signature-vs-click while entering line items — they decide it when they
   * decide who they are sending to, which is here.
   *
   * Tri-state, deliberately: `undefined` means the caller does not use this
   * feature (no picker), `''` means the picker is on with nothing chosen (send
   * leaves the stored method alone), and a value is an explicit choice.
   */
  approvalMethod?: CustomerApprovalMethod | '';
  onApprovalMethodChange?: (value: CustomerApprovalMethod | '') => void;
  /**
   * Restrict the picker to the methods this document can honour. A proposal
   * page can only take a signature or a click, so it passes those two. With
   * options given, the blank value reads "Company default" — the server
   * applies the tenant's default on send — and is offered only while the
   * value is blank, so a chosen method can't be un-chosen back to it.
   */
  approvalMethodOptions?: readonly CustomerApprovalMethod[];
  showSignature?: boolean;
  requireSignature?: boolean;
  onRequireSignatureChange?: (value: boolean) => void;
  confirmLabel?: string;
  cancelLabel?: string;
  isPending?: boolean;
  /** Hold the confirm button while the caller is still resolving what to send. */
  confirmDisabled?: boolean;
  /** Optional explanation supplied by the document-specific send gate. */
  disabledReason?: string | null;
  onConfirm: (channels: SendChannels) => void;
  onCancel: () => void;
};

export function SendConfirmModal({
  open,
  title,
  customerName,
  customerEmail,
  customerPhone,
  approvalMethod,
  onApprovalMethodChange,
  approvalMethodOptions,
  showSignature = false,
  requireSignature,
  onRequireSignatureChange,
  confirmLabel = 'Send',
  cancelLabel = 'Cancel',
  isPending = false,
  confirmDisabled = false,
  disabledReason,
  onConfirm,
  onCancel,
}: Props) {
  const [sendEmail, setSendEmail] = useState(() => !!customerEmail);
  const [sendSms, setSendSms] = useState(() => !!customerPhone);

  useEffect(() => {
    if (!open) return;
    setSendEmail(!!customerEmail);
    setSendSms(!!customerPhone);
  }, [customerEmail, customerPhone, open]);

  if (!open) return null;

  const canSendEmail = !!customerEmail;
  const canSendSms = !!customerPhone;
  const channels: SendChannels = [
    ...(sendEmail && canSendEmail ? (['email'] as const) : []),
    ...(sendSms && canSendSms ? (['sms'] as const) : []),
  ];
  const canSend = channels.length > 0;
  // One flag for both the picker and the signature toggle, so a caller that
  // supplies half the pair can never get the new toggle semantics with no
  // picker to drive them.
  const picksApprovalMethod = approvalMethod !== undefined && !!onApprovalMethodChange;

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <Card className="w-full max-w-md space-y-4 p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xs p-1 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground">
          To <span className="font-medium text-foreground">{customerName}</span>
        </p>

        {/* Channel selection */}
        <div className="space-y-1.5">
          <p className="text-sm font-medium">Send via</p>
          <div className="overflow-hidden rounded-md border">
            <label
              className={`flex cursor-pointer items-center gap-3 p-3 transition-colors ${
                !canSendEmail ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted/30'
              }`}
            >
              <Checkbox
                checked={sendEmail && canSendEmail}
                onCheckedChange={(v) => setSendEmail(!!v)}
                disabled={!canSendEmail}
              />
              <Mail className="size-4 shrink-0 text-muted-foreground" />
              <span className="text-sm font-medium">Email</span>
              <span className="ml-auto min-w-0 truncate text-xs text-muted-foreground">
                {customerEmail || 'No email on file'}
              </span>
            </label>

            <label
              className={`flex cursor-pointer items-center gap-3 border-t p-3 transition-colors ${
                !canSendSms ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted/30'
              }`}
            >
              <Checkbox
                checked={sendSms && canSendSms}
                onCheckedChange={(v) => setSendSms(!!v)}
                disabled={!canSendSms}
              />
              <MessageSquare className="size-4 shrink-0 text-muted-foreground" />
              <span className="text-sm font-medium">SMS</span>
              <span className="ml-auto min-w-0 truncate text-xs text-muted-foreground">
                {customerPhone ? formatPhoneDisplay(customerPhone) : 'No phone on file'}
              </span>
            </label>
          </div>
        </div>

        {/* NOT-584 (A4) — approval method, decided at the send step */}
        {picksApprovalMethod && (
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Approval method</p>
            <Select
              value={approvalMethod === '' ? 'none' : approvalMethod}
              onValueChange={(value) =>
                onApprovalMethodChange?.(value === 'none' ? '' : (value as CustomerApprovalMethod))
              }
            >
              <SelectTrigger className="w-full" aria-label="Approval method">
                <SelectValue placeholder="Not specified" />
              </SelectTrigger>
              <SelectContent>
                {approvalMethodOptions === undefined ? (
                  <SelectItem value="none">Not specified</SelectItem>
                ) : approvalMethod === '' ? (
                  <SelectItem value="none">Company default</SelectItem>
                ) : null}
                {Object.entries(ESTIMATE_APPROVAL_METHOD_LABELS)
                  .filter(
                    ([method]) =>
                      approvalMethodOptions === undefined ||
                      approvalMethodOptions.includes(method as CustomerApprovalMethod),
                  )
                  .map(([method, label]) => (
                    <SelectItem key={method} value={method}>
                      {label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {/*
              Email and Verbal take the Approve and Decline buttons off the
              customer's copy entirely — the customer answers off-page and
              somebody here records it. That is a real choice, but it is not one
              the picker's label implies, and a sender who makes it by accident
              only finds out when the customer says the link does nothing.
            */}
            {approvalMethod === 'SIGNATURE' && !onRequireSignatureChange && (
              <p className="text-xs text-muted-foreground">
                The customer types their name and draws a signature on the page to accept.
              </p>
            )}
            {(approvalMethod === 'EMAIL' || approvalMethod === 'VERBAL') && (
              // Channel-neutral on purpose. The customer's copy leads with the
              // method's own channel but falls through to whatever contact
              // detail the merchant actually has on file, so naming one here
              // would teach the sender an outcome this modal cannot promise —
              // it never sees the company record.
              <p className="text-xs text-muted-foreground">
                The customer won&apos;t be able to approve or decline online — their copy will ask
                them to reach you directly, and you record their answer here.
              </p>
            )}
          </div>
        )}

        {/*
          Signature toggle. When the picker is present it is the live source of
          truth — reading a stored method here would leave the toggle stale the
          moment someone changes the method in this very modal. Gated on the
          same flag as the picker so the two can never disagree.
        */}
        {(picksApprovalMethod ? approvalMethod === 'SIGNATURE' : showSignature) &&
          onRequireSignatureChange && (
            <div className="flex items-center justify-between gap-3 rounded-md border bg-secondary p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">Require signature</p>
                <p className="text-xs text-muted-foreground">
                  {requireSignature ? 'Customer must sign to approve' : 'Auto-approve on send'}
                </p>
              </div>
              <Switch checked={requireSignature} onCheckedChange={onRequireSignatureChange} />
            </div>
          )}

        {disabledReason ? (
          <p role="alert" className="text-sm text-destructive">
            {disabledReason}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button
            size="sm"
            onClick={() => onConfirm(channels)}
            disabled={!canSend || isPending || confirmDisabled}
          >
            {isPending ? (
              <Loader2 className="mr-1 size-3.5 animate-spin" />
            ) : (
              <Send className="mr-1 size-3.5" />
            )}
            {confirmLabel}
          </Button>
        </div>
      </Card>
    </div>,
    document.body,
  );
}
